import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { GestureDetector, Gesture } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  interpolate,
  Extrapolate,
  SharedValue,
  withTiming,
} from 'react-native-reanimated';
import React, {
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';

import {
  SPRING_CONFIG,
  useSheetDimensions,
  useSheetPosition,
  SHEET_BACKGROUND,
  CONTENT_BACKGROUND,
  CONTAINER_BACKGROUND,
  SHEET_BORDER_RADIUS,
  HANDLE_WIDTH,
  HANDLE_HEIGHT,
  HANDLE_MARGIN,
  HANDLE_BACKGROUND,
  BUTTON_SIZE,
  BUTTON_BACKGROUND,
  BUTTON_OPACITY,
} from './constants';

// Initial Setup Demo
export const InitialSetup = () => {
  return (
    <View style={styles.container}>
      <StatusBar style="light" />
    </View>
  );
};

// Basic Component Demo
type BottomSheetProps = {
  children?: React.ReactNode;
  hasBackdrop?: boolean;
};

const BasicBottomSheet: React.FC<BottomSheetProps> = ({ children }) => {
  const { sheetWidth, sheetHeight } = useSheetDimensions();
  const gesture = Gesture.Pan();

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        style={[
          styles.bottomSheetContainer,
          {
            height: sheetHeight,
            width: sheetWidth,
          },
        ]}
      >
        <View style={styles.line} />
        {children}
      </Animated.View>
    </GestureDetector>
  );
};

export const BasicComponent = () => {
  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <BasicBottomSheet>
        <View style={{ flex: 1, backgroundColor: CONTENT_BACKGROUND }} />
      </BasicBottomSheet>
    </View>
  );
};

// Pan Gesture Demo
const PanBottomSheet: React.FC<BottomSheetProps> = ({ children }) => {
  const { sheetWidth, sheetHeight } = useSheetDimensions();
  const { bottomPosition, topPosition, threshold } = useSheetPosition();
  const translateY = useSharedValue(bottomPosition);
  const context = useSharedValue({ y: 0 });

  const gesture = Gesture.Pan()
    .onStart(() => {
      context.value = { y: translateY.value };
    })
    .onUpdate((event) => {
      translateY.value = event.translationY + context.value.y;
      translateY.value = Math.max(translateY.value, topPosition);
    })
    .onEnd(() => {
      if (translateY.value > threshold) {
        translateY.value = withSpring(bottomPosition, SPRING_CONFIG);
      } else {
        translateY.value = withSpring(topPosition, SPRING_CONFIG);
      }
    });

  const rBottomSheetStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        style={[
          styles.bottomSheetContainer,
          {
            height: sheetHeight,
            width: sheetWidth,
          },
          rBottomSheetStyle,
        ]}
      >
        <View style={styles.line} />
        {children}
      </Animated.View>
    </GestureDetector>
  );
};

export const PanGesture = () => {
  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <PanBottomSheet>
        <View style={{ flex: 1, backgroundColor: CONTENT_BACKGROUND }} />
      </PanBottomSheet>
    </View>
  );
};

// Border Radius Demo
const BorderRadiusBottomSheet: React.FC<BottomSheetProps> = ({ children }) => {
  const { sheetWidth, sheetHeight } = useSheetDimensions();
  const { bottomPosition, topPosition, threshold } = useSheetPosition();
  const translateY = useSharedValue(bottomPosition);
  const context = useSharedValue({ y: 0 });

  const gesture = Gesture.Pan()
    .onStart(() => {
      context.value = { y: translateY.value };
    })
    .onUpdate((event) => {
      translateY.value = event.translationY + context.value.y;
      translateY.value = Math.max(translateY.value, topPosition);
    })
    .onEnd(() => {
      if (translateY.value > threshold) {
        translateY.value = withSpring(bottomPosition, SPRING_CONFIG);
      } else {
        translateY.value = withSpring(topPosition, SPRING_CONFIG);
      }
    });

  const rBottomSheetStyle = useAnimatedStyle(() => {
    const borderRadius = interpolate(
      translateY.value,
      [topPosition, bottomPosition],
      [SHEET_BORDER_RADIUS.min, SHEET_BORDER_RADIUS.max],
      Extrapolate.CLAMP
    );

    return {
      borderRadius,
      transform: [{ translateY: translateY.value }],
    };
  });

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View
        style={[
          styles.bottomSheetContainer,
          {
            height: sheetHeight,
            width: sheetWidth,
            transform: [{ translateX: -sheetWidth / 2 }],
          },
          rBottomSheetStyle,
        ]}
      >
        <View style={styles.line} />
        {children}
      </Animated.View>
    </GestureDetector>
  );
};

export const BorderRadius = () => {
  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <BorderRadiusBottomSheet>
        <View style={{ flex: 1, backgroundColor: CONTENT_BACKGROUND }} />
      </BorderRadiusBottomSheet>
    </View>
  );
};

// Final Demo with External Control
export type BottomSheetRefProps = {
  scrollTo: (destination: number) => void;
  isActive: () => boolean;
};

const AnimatedBackdrop: React.FC<{
  active: SharedValue<boolean>;
  onTap: () => void;
}> = ({ active, onTap }) => {
  const rBackdropStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(active.value ? 1 : 0, { duration: 200 }),
    };
  });

  return (
    <button onClick={onTap} className="absolute inset-0">
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          stylesBackdrop.container,
          rBackdropStyle,
        ]}
      />
    </button>
  );
};

const stylesBackdrop = StyleSheet.create({
  container: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
});

const FinalBottomSheet = forwardRef<BottomSheetRefProps, BottomSheetProps>(
  ({ children, hasBackdrop = true }, ref) => {
    const { sheetWidth, sheetHeight } = useSheetDimensions();
    const { bottomPosition, topPosition, threshold } = useSheetPosition();
    const translateY = useSharedValue(bottomPosition);
    const active = useSharedValue(false);
    const context = useSharedValue({ y: 0 });

    const scrollTo = useCallback(
      (destination: number) => {
        'worklet';
        active.value = destination !== bottomPosition;
        translateY.value = withSpring(destination, SPRING_CONFIG);
      },
      [active, bottomPosition, translateY]
    );

    const isActive = useCallback(() => {
      return active.value;
    }, [active]);

    useImperativeHandle(ref, () => ({ scrollTo, isActive }), [
      scrollTo,
      isActive,
    ]);

    const gesture = Gesture.Pan()
      .onStart(() => {
        context.value = { y: translateY.value };
      })
      .onUpdate((event) => {
        translateY.value = event.translationY + context.value.y;
        translateY.value = Math.max(translateY.value, topPosition);
      })
      .onEnd(() => {
        if (translateY.value > threshold) {
          scrollTo(bottomPosition);
        } else {
          scrollTo(topPosition);
        }
      });

    const rBottomSheetStyle = useAnimatedStyle(() => {
      const borderRadius = interpolate(
        translateY.value,
        [topPosition, bottomPosition],
        [SHEET_BORDER_RADIUS.min, SHEET_BORDER_RADIUS.max],
        Extrapolate.CLAMP
      );

      return {
        borderRadius,
        transform: [{ translateY: translateY.value }],
      };
    });

    return (
      <>
        {hasBackdrop && (
          <AnimatedBackdrop
            active={active}
            onTap={() => {
              scrollTo(bottomPosition);
            }}
          />
        )}
        <GestureDetector gesture={gesture}>
          <Animated.View
            style={[
              styles.bottomSheetContainer,
              {
                height: sheetHeight,
                width: sheetWidth,
                transform: [{ translateX: -sheetWidth / 2 }],
              },
              rBottomSheetStyle,
            ]}
          >
            <View style={styles.line} />
            {children}
          </Animated.View>
        </GestureDetector>
      </>
    );
  }
);

type FinalDemoProps = {
  hasBackdrop?: boolean;
};

export const FinalDemo: React.FC<FinalDemoProps> = ({ hasBackdrop = true }) => {
  const ref = useRef<BottomSheetRefProps>(null);
  const { bottomPosition, topPosition } = useSheetPosition();

  const onPress = useCallback(() => {
    const isActive = ref?.current?.isActive();
    if (isActive) {
      ref?.current?.scrollTo(bottomPosition);
    } else {
      ref?.current?.scrollTo(topPosition);
    }
  }, [bottomPosition, topPosition]);

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <TouchableOpacity style={styles.button} onPress={onPress} />
      <FinalBottomSheet ref={ref} hasBackdrop={hasBackdrop}>
        <View style={{ flex: 1, backgroundColor: CONTENT_BACKGROUND }} />
      </FinalBottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CONTAINER_BACKGROUND,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomSheetContainer: {
    position: 'absolute',
    backgroundColor: SHEET_BACKGROUND,
    borderRadius: SHEET_BORDER_RADIUS.max,
    zIndex: 100,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  line: {
    width: HANDLE_WIDTH,
    height: HANDLE_HEIGHT,
    backgroundColor: HANDLE_BACKGROUND,
    alignSelf: 'center',
    marginVertical: HANDLE_MARGIN,
    borderRadius: HANDLE_HEIGHT / 2,
  },
  button: {
    height: BUTTON_SIZE,
    aspectRatio: 1,
    backgroundColor: BUTTON_BACKGROUND,
    borderRadius: BUTTON_SIZE / 2,
    opacity: BUTTON_OPACITY,
    zIndex: 50,
  },
});
